import { StandardEvents } from '@shopify/events';

class ProductThumbnailCarousel extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    this.addEventListener('click', this.#handleClick, { signal });
    target?.addEventListener('slideshow:select', this.#handleSlideshowSelect, { signal });
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    this.#syncFromGallery();
    this.#updateAvailability();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-media-index]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const index = Number.parseInt(button.dataset.mediaIndex || '', 10);
    if (Number.isNaN(index)) return;

    const slideshow = this.#gallerySlideshow;
    if (!slideshow || typeof slideshow.select !== 'function') return;

    // A chosen detail should appear immediately, without travelling through the carousel.
    slideshow.select(index, event, { animate: false });
    this.#setSelected(index);
  };

  #handleSlideshowSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.product-information__media')) return;

    const index = event.detail?.index;
    if (typeof index !== 'number') return;

    this.#setSelected(index);
  };

  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;

        const updatedCarousel = Array.from(html.querySelectorAll('product-thumbnail-carousel')).find(
          (carousel) => carousel instanceof HTMLElement && carousel.dataset.blockId === this.dataset.blockId
        );
        if (!updatedCarousel) return;

        this.replaceWith(updatedCarousel);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-thumbnail-carousel] Event promise rejected:', error);
      });
  };

  #syncFromGallery() {
    const slideshow = this.#gallerySlideshow;
    if (!slideshow) return;

    const current = typeof slideshow.current === 'number' ? slideshow.current : 0;
    this.#setSelected(current);
  }

  #setSelected(index) {
    const selectedButton = this.querySelector(`[data-media-index="${index}"]`);

    for (const button of this.querySelectorAll('[data-media-index]')) {
      button.setAttribute('aria-selected', `${button === selectedButton}`);
    }

    this.#scrollSelectedWithinTrack(selectedButton);
  }

  #scrollSelectedWithinTrack(selectedButton) {
    if (!(selectedButton instanceof HTMLElement)) return;

    const track = selectedButton.closest('.product-thumbnail-carousel__track');
    if (!(track instanceof HTMLElement)) return;

    // On desktop this track is a grid with overflow visible, so no scrolling is needed.
    const isScrollable = track.scrollWidth > track.clientWidth;
    if (!isScrollable) return;

    const trackRect = track.getBoundingClientRect();
    const buttonRect = selectedButton.getBoundingClientRect();

    const currentScrollLeft = track.scrollLeft;
    const buttonCenterWithinTrack = buttonRect.left - trackRect.left + currentScrollLeft + buttonRect.width / 2;
    const targetScrollLeft = buttonCenterWithinTrack - track.clientWidth / 2;
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const clampedScrollLeft = Math.min(maxScrollLeft, Math.max(0, targetScrollLeft));

    track.scrollTo({
      left: clampedScrollLeft,
      behavior: 'smooth',
    });
  }

  #updateAvailability() {
    this.hidden = !this.#gallerySlideshow;
  }

  get #gallerySlideshow() {
    return this
      .closest('product-component')
      ?.querySelector('.product-information__media media-gallery.media-gallery--carousel slideshow-component');
  }
}

if (!customElements.get('product-thumbnail-carousel')) {
  customElements.define('product-thumbnail-carousel', ProductThumbnailCarousel);
}
